import type { Item, Bundle, ProductCategoryFilter } from 'types';

type SubcategoryDto = {
  displayName: string;
  slug: string;
  itemFilter: (item: Item) => boolean;
};

export type CategoryDto = {
  name: string;
  displayName: string;
  categoryFilters: ProductCategoryFilter[];
  slug: string | null;
  subcategories?: SubcategoryDto[];
};

export type CategoryDtoWithSlug = CategoryDto & { slug: string };

export const isSubcategoryPath = (pathname: string) => {
  const parts = pathname.split('/').filter((part) => part.trim() !== '');

  if (parts.length < 3 || parts[0] !== 'collection') {
    return false;
  }

  return isValidSubcategory(parts[1], parts[2]);
};

export const isValidSubcategory = (category: string, slug: string) => {
  return categories.some((c) => {
    if (c.slug !== category) {
      return false;
    }

    const subcategories = c.subcategories ?? [];

    return subcategories.some((subcategory) => subcategory.slug === slug);
  });
};

export const subcategories = [
  {
    displayName: "Men's Tuxedo Rental",
    slug: 'tuxedos',
    itemFilter: (bundle: Bundle) => bundle.type?.toLowerCase() === 'tuxedo',
  },
  {
    displayName: "Men's Blue Suits and Tuxedos",
    slug: 'blue-suits',
    itemFilter: (bundle: Bundle) => bundle.color?.toLowerCase() === 'blue',
  },
  {
    displayName: "Men's Black Suits and Tuxedos",
    slug: 'black-suits',
    itemFilter: (bundle: Bundle) => bundle.color?.toLowerCase() === 'black',
  },
  {
    displayName: "Men's Gray Suits and Tuxedos",
    slug: 'gray-suits',
    itemFilter: (bundle: Bundle) => bundle.color?.toLowerCase() === 'gray' || bundle.color?.toLowerCase() === 'grey',
  },
  {
    displayName: "Men's Tan Suits and Tuxedos",
    slug: 'tan-suits',
    itemFilter: (bundle: Bundle) => bundle.color?.toLowerCase() === 'tan',
  },
];

const categories: CategoryDto[] = [
  {
    name: 'preconfigured',
    displayName: 'Tuxedos & Suits',
    categoryFilters: [
      { displayName: 'Tux', value: 'Tux' },
      { displayName: 'Suit', value: 'Suit' },
    ],
    slug: 'tuxedos-and-suits',
    subcategories: subcategories,
  },
  {
    name: 'Shirt',
    displayName: 'Shirts',
    categoryFilters: [
      { displayName: 'Twill', value: 'Twill' },
      { displayName: 'Pleated', value: 'Pleated' },
    ],
    slug: 'shirts',
  },
  {
    name: 'Tie',
    displayName: 'Ties',
    categoryFilters: [
      { displayName: 'Long', value: (str: string) => !str.includes('Bow') },
      { displayName: 'Bow', value: 'Bow' },
      { displayName: 'Skinny', value: 'Skinny' },
    ],
    slug: 'ties',
  },
  {
    name: 'Vest,Cummerbund',
    displayName: 'Vests & Cummerbunds',
    categoryFilters: [
      { displayName: 'Vest', value: 'Vest' },
      { displayName: 'Cummerbund', value: 'Cummerbund' },
    ],
    slug: 'vests-and-cummerbunds',
  },
  {
    name: 'Shoe',
    displayName: 'Shoes',
    categoryFilters: [],
    slug: null,
  },
  {
    name: 'Pocket Square',
    displayName: 'Pocket Squares',
    categoryFilters: [],
    slug: null,
  },
  {
    name: 'Socks',
    displayName: 'Socks',
    categoryFilters: [],
    slug: null,
  },
  {
    name: 'Belt,Suspenders',
    displayName: 'Belts & Suspenders',
    categoryFilters: [
      { displayName: 'Belt', value: 'Belt' },
      { displayName: 'Suspenders', value: 'Suspenders' },
    ],
    slug: null,
  },
  {
    name: 'Lapel Pin',
    displayName: 'Lapel Pins',
    categoryFilters: [
      { displayName: 'Rose', value: 'Rose' },
      { displayName: 'Carnation', value: 'Carnation' },
    ],
    slug: null,
  },
  {
    name: 'Cufflinks',
    displayName: 'Cufflinks',
    categoryFilters: [],
    slug: null,
  },
  {
    name: 'Tie Bar',
    displayName: 'Tie Bars',
    categoryFilters: [],
    slug: null,
  },
  {
    name: 'Suspenders,Belt,Lapel Pin,Tie Bar,Cufflinks,Pocket Square',
    displayName: 'Accessories',
    categoryFilters: [
      { displayName: 'Tie Bars', value: 'Tie Bar' },
      { displayName: 'Cufflinks', value: 'Cufflinks' },
      { displayName: 'Lapel Pins', value: 'Lapel Pin' },
      { displayName: 'Pocket Squares', value: 'Pocket Square' },
      { displayName: 'Belt', value: 'Belt' },
      { displayName: 'Suspenders', value: 'Suspenders' },
    ],
    slug: 'accessories',
  },
  {
    name: 'Shoe,Socks',
    displayName: 'Shoes & Socks',
    categoryFilters: [
      { displayName: 'Shoes', value: 'Shoe' },
      { displayName: 'Socks', value: 'Socks' },
    ],
    slug: 'shoes-and-socks',
  },
];

export default categories;
